import React from 'react';
import { t } from '@lingui/macro';
import { Button, Field, Spinner } from '@economic/taco';
import { Plan, useCurrentAgreement, useCurrentUser, useLegacyFrame } from '@economic/react-app';

import useQuery from '../../../../shared/hooks/useQuery';
import EditCompanyInfoDialog from '../dialogs/EditCompanyInfoDialog';
import { Column, Row } from '../../../../shared/components/Grid/Grid';
import { IsValidDanishCVR } from '../../../../shared/util/utilities/cvrUtils';

export const isCvrReadonly = (company, cvr, subscription) => {
    return !!company.cvr && IsValidDanishCVR(cvr) && !subscription.trial && (subscription.isInvoiced || company.hasNemHandel);
};

export default function CompanyInfo() {
    const user = useCurrentUser();
    const { number: agreementNumber, subscription, cvr } = useCurrentAgreement();
    const isAdministrator = user.isAdministrator && agreementNumber === user.agreement.number;
    const isInternalAgreement = subscription.level === Plan.Internal;
    const companyQuery = useQuery<any>('/company/getCurrentCompany');
    const adminInfoQuery = useQuery<any>('/administrator/getCurrentAdministratorOffice', {}, { skip: !isAdministrator });
    const firebirdReady = useLegacyFrame();

    if (companyQuery.loading || (isAdministrator && adminInfoQuery.loading) || !firebirdReady) {
        return <Spinner className="flex" />;
    }

    const company = isAdministrator ? adminInfoQuery?.data?.model : companyQuery?.data?.model;

    return (
        <>
            <section>
                <h3>
                    {t`Company`}
                    <Button
                        appearance="discrete"
                        className="text-base -ml-1"
                        dialog={props => (
                            <EditCompanyInfoDialog
                                initialData={company}
                                onSubmitted={() => {
                                    isAdministrator ? adminInfoQuery.refetch() : companyQuery.refetch();
                                }}
                                isAdministrator={isAdministrator}
                                isCvrReadonly={isCvrReadonly(company, cvr, subscription)}
                                isInternalAgreement={isInternalAgreement}
                                isAdministratorPayingForCurrentAgreement={
                                    companyQuery.data?.isAdministratorPayingForCurrentAgreement
                                }
                                enforce2FAReason={companyQuery.data?.enforce2FAReason}
                                shouldDisable2FA={companyQuery.data?.shouldDisable2FA}
                                {...props}
                            />
                        )}>
                        {t`Edit`}
                    </Button>
                </h3>
                <Row divided>
                    <Column xs="3">
                        <Field>
                            {t`Name and address`}
                            <span className="font-normal">{company?.name || ''}</span>
                            <span className="font-normal">{company?.address1}</span>
                            <span className="font-normal">{company?.address2}</span>
                            <span className="font-normal">{company?.zipCode + ' ' + company?.city}</span>
                            <span className="font-normal">{company?.country}</span>
                        </Field>
                        {!isAdministrator && (
                            <>
                                <Field>
                                    {t`Co. reg. no.`}
                                    <span className="font-normal">{company?.cvr}</span>
                                </Field>
                            </>
                        )}
                    </Column>
                    <Column xs="3">
                        {!!company?.phone && (
                            <Field>
                                {t`Telephone`}
                                <span className="font-normal">{company.phone || ''}</span>
                            </Field>
                        )}
                        {!!company?.mobilePhone && (
                            <Field>
                                {t`Mobile`}
                                <span className="font-normal">{company.mobilePhone || ''}</span>
                            </Field>
                        )}
                        {!!company?.fax && (
                            <Field>
                                {t`Fax`}
                                <span className="font-normal">{company.fax || ''}</span>
                            </Field>
                        )}
                    </Column>
                    <Column xs="6">
                        {!!company?.email && (
                            <Field>
                                {t`Contact email`}
                                <span className="font-normal">{company.email || ''}</span>
                            </Field>
                        )}
                        {!!company?.homepage && (
                            <Field>
                                {t`Website`}
                                <span className="font-normal">{company.homepage || ''}</span>
                            </Field>
                        )}
                    </Column>
                </Row>
            </section>
        </>
    );
}
